// GAP-1 (JW8 background-reception audit §3): when the master tab receives a
// `call.invite` over the WSS while the tab is HIDDEN and the Web Push path is
// unavailable (push service blocked by censorship, but Notification permission
// still granted), nothing raises an OS-level alert — every `showNotification`
// today lives in the SW push handler, so a WSS-only invite rings a surface the
// user can neither see (tab hidden) nor hear (AudioContext suspended). This
// installs a page-side backstop: on a hidden, ringing, permission-granted
// incoming call it fires the notification the SW would for `incoming_call`,
// with the same `tag: callId` (so the platform collapses it with any
// push-driven notification into one — no double ring) and the same
// data/actions, so the SW's existing notificationclick/close-by-tag paths own
// it unchanged. When the ring ends (answered / declined / cancelled / timeout)
// the engine's call state clears, and we close the notification by tag.
//
// PERSONALIZATION (privacy-preserving): because this notification is raised by
// the PAGE — not the SW push handler — it can name the caller and show their
// avatar, resolved LOCALLY from the engine's peerUserId + the cached friends
// list. That identity never touches push infrastructure (the push payload
// stays {type, callId}); it only enriches a notification the device raises for
// itself. When the peer isn't a known friend, it falls back to the generic
// (no-name) text — identical to the SW/app-closed notification.

import { notificationForPush, type NotificationCaps, type NotificationSpec } from '../sw/swCore.js';
import { getLang, type Lang } from '../i18n/core.js';
import type { ReadableStore } from '@merkury/brain/call/store.js';
import type { CallEngineState } from '@merkury/brain/call/types.js';

/** The slice of the call engine the hidden ring observes (structural). */
export interface HiddenRingEngine {
  readonly state: ReadableStore<CallEngineState>;
}

/** The slice of a ServiceWorkerRegistration the page-side ring needs. */
export interface HiddenRingRegistration {
  showNotification(title: string, options: NotificationSpec['options']): Promise<void>;
  getNotifications(filter: { tag: string }): Promise<Array<{ close(): void }>>;
}

/** A caller resolved locally for personalization; never sourced from push. */
export interface HiddenRingPeer {
  /** Display name for the notification title; absent ⇒ generic (no-name) text. */
  name?: string;
  /** Avatar candidate index, for the notification icon; absent ⇒ default icon. */
  avatarSeed?: number;
}

export interface HiddenRingDeps {
  engine: HiddenRingEngine;
  /** Page-visibility source: the OS ring shows ONLY while `hidden` (a visible
   *  tab already renders the in-app RingSurface). Reads `document.hidden`. */
  doc: Pick<Document, 'hidden'>;
  /** Resolves the active SW registration (e.g. `navigator.serviceWorker.ready`);
   *  null when unavailable, in which case the ring is a no-op. */
  getRegistration: () => Promise<HiddenRingRegistration | null>;
  /** Notification-permission gate; defaults to the real Notification API. Note
   *  this is independent of the push *subscription* — permission can be granted
   *  while the push service is blocked, which is exactly the gap this closes. */
  permission?: () => NotificationPermission;
  /** App language for the notification text; defaults to the page's current
   *  language so it matches the SW's persisted-language read. */
  lang?: () => Lang;
  /** Platform notification capabilities (action-button / silent-push budget);
   *  defaults to reading `Notification.maxActions`, matching sw.ts. */
  caps?: () => NotificationCaps;
  /** LOCAL caller lookup for personalization (name + avatar). Returns null for
   *  an unknown peer ⇒ generic notification. Never involves the push payload. */
  resolvePeer?: (peerUserId: string) => HiddenRingPeer | null;
  /** LOCAL avatar → data-URL for the notification icon; undefined ⇒ default. */
  avatarIcon?: (peerUserId: string, avatarSeed: number) => string | undefined;
}

/** Mirror of sw.ts `notificationCaps`: Safari/WebKit exposes no `maxActions` and
 *  has no silent-push budget; Chrome/Edge/Firefox report a positive count. */
function detectCaps(): NotificationCaps {
  const ctor = (typeof Notification !== 'undefined' ? Notification : undefined) as
    | { maxActions?: number }
    | undefined;
  return { supportsActions: typeof ctor?.maxActions === 'number' && ctor.maxActions > 0 };
}

/**
 * Subscribe to the engine and raise/dismiss a page-side OS notification for a
 * hidden-tab incoming ring. Returns a teardown that unsubscribes and closes any
 * still-open notification. Idempotent per callId: a notification is shown once
 * and closed once.
 */
export function installHiddenRing(deps: HiddenRingDeps): () => void {
  const permission = deps.permission ?? ((): NotificationPermission => Notification.permission);
  const lang = deps.lang ?? getLang;
  const caps = deps.caps ?? detectCaps;

  let shownCallId: string | null = null;
  let disposed = false;

  const closeTag = async (tag: string): Promise<void> => {
    const reg = await deps.getRegistration();
    if (!reg) return;
    const open = await reg.getNotifications({ tag });
    for (const note of open) note.close();
  };

  const showRing = async (callId: string, peerUserId: string): Promise<void> => {
    const reg = await deps.getRegistration();
    if (!reg || disposed) return;
    // A live engine ring is definitionally fresh (no `sentAt`), so this always
    // yields the `incoming_call` spec — the SAME tag/data/actions the push path
    // builds, which the SW's click/close handlers already own.
    const spec = notificationForPush({ type: 'incoming_call', callId }, lang(), Date.now(), caps());
    if (spec === null) return;
    // Personalize title + icon from LOCAL data only; the generic spec is the
    // fallback for an unknown peer. Body keeps the generic "Incoming call"
    // heading so the layout reads: <name> / origin / Incoming call.
    const peer = deps.resolvePeer?.(peerUserId);
    if (peer?.name) {
      spec.options.body = spec.title;
      spec.title = peer.name;
      if (peer.avatarSeed !== undefined) {
        const icon = deps.avatarIcon?.(peerUserId, peer.avatarSeed);
        if (icon) spec.options.icon = icon;
      }
    }
    await reg.showNotification(spec.title, spec.options);
  };

  const sync = (): void => {
    if (disposed) return;
    const call = deps.engine.state.get().call;
    const ringing = call !== null && call.phase === 'ringing' && call.direction === 'incoming';
    if (ringing && deps.doc.hidden && permission() === 'granted') {
      if (shownCallId !== call.callId) {
        const prev = shownCallId;
        shownCallId = call.callId;
        if (prev !== null) void closeTag(prev);
        void showRing(call.callId, call.peerUserId);
      }
    } else if (shownCallId !== null) {
      const prev = shownCallId;
      shownCallId = null;
      void closeTag(prev);
    }
  };

  const unsub = deps.engine.state.subscribe(sync);
  sync(); // an invite may already be ringing when this installs

  return () => {
    disposed = true;
    unsub();
    if (shownCallId !== null) {
      const prev = shownCallId;
      shownCallId = null;
      void closeTag(prev);
    }
  };
}
